import type {
  CampaignGiftDetailData,
  CampaignOrder,
  CampaignOrderProduct,
  CampaignGiftDetailRequest,
  CampaignGiftDetailResponse,
  CategoryTreeResponse,
  GiftingCollectionListItem,
  OCAPIProductDetailResponse,
  ProductSearchRequest,
  ProductSearchResponse,
} from '@dashhudson/api-client/creator/model';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { env } from '@/env';

export const http = createDashHttpClient(env.creatorsApiUrl, {
  camelizeKeys: true,
});

export type GiftDetail = CampaignGiftDetailData;
export type GiftingCollection = GiftingCollectionListItem;
export type CampaignGiftOrder = CampaignOrder;
export type CampaignGiftOrderProduct = CampaignOrderProduct;

export const GIFT_ORDER_STATUS = {
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  ORDER_PLACED: 'order_placed',
  FAILED: 'failed',
} as const;

export const GIFT_ORDER_PRODUCT_STATUS = {
  OK: 'ok',
  OUT_OF_STOCK: 'out_of_stock',
  NOT_LIVE: 'not_live',
  DELETED: 'deleted',
} as const satisfies Record<string, NonNullable<CampaignGiftOrderProduct['status']>>;

export async function getGiftDetail(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<{ data: GiftDetail }>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/gift_detail`,
    config,
  );
}

export async function upsertGiftDetail(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  body: CampaignGiftDetailRequest,
  config: HttpRequestConfig = {},
) {
  return http.put<CampaignGiftDetailResponse>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/gift_detail`,
    body,
    config,
  );
}

export async function updateGiftDetail(
  { brandId, campaignId, ordersOpen }: { brandId: number; campaignId: number; ordersOpen: boolean },
  config: HttpRequestConfig = {},
) {
  return http.put<{ data: GiftDetail }>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/gift_detail`,
    { ordersOpen },
    config,
  );
}

export async function getGiftingCollections(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<{ data: GiftingCollection[] }>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/collections`,
    config,
  );
}

export async function getCampaignGiftOrders(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<{ data: CampaignGiftOrder[] }>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/orders`,
    config,
  );
}

export async function deleteGiftingCollection(
  { brandId, collectionId }: { brandId: number; collectionId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(`/brands/${brandId}/gifting/collections/${collectionId}`, config);
}

export async function getGiftingCategories(
  { brandId }: { brandId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<CategoryTreeResponse>(`/brands/${brandId}/gifting/ocapi/categories`, config);
}

export async function searchGiftingProducts(
  { brandId }: { brandId: number },
  body: ProductSearchRequest,
  config: HttpRequestConfig = {},
) {
  return http.post<ProductSearchResponse>(
    `/brands/${brandId}/gifting/ocapi/product_search`,
    body,
    config,
  );
}

export async function getGiftingProduct(
  { brandId, productId }: { brandId: number; productId: string },
  config: HttpRequestConfig = {},
) {
  return http.get<OCAPIProductDetailResponse>(
    `/brands/${brandId}/gifting/ocapi/products/${productId}`,
    config,
  );
}
